import React, { ReactNode } from 'react';
import { ClickRightUniversal } from '@duxton-web/icons';
import { Collapse, Flex, Spin, Typography } from '@duxton-web/ui';
import { CredentialResponse } from '@react-oauth/google';
import jwt_decode from 'jwt-decode';

import { redirectTo } from '@/common/environment';
import Service from '@/lib/App';

import './LoginPage.css';

import type { CollapseProps } from 'antd';
import * as lscache from 'lscache';
import styled from 'styled-components';

import { ConcedoLoginButton } from './ConcedoLogin';
import { SSOLogin } from './SSOLogin';
import {
  CONCEDO_CLIENT_ID,
  CONCEDO_CODE_VERIFIER,
  CONCEDO_REDIRECT_URI,
  CONCEDO_STATE,
  REDIRECT_USER_REQUEST_URI,
} from '@/common/const';
import { GoogleInfo } from '@/common/dto';
import { getSource, getTenant, isSSOLoginTenant } from '@/common/Tenants';
import { defaultPresetColors } from '@/theme';
import { toQueryString } from '@/utils/url';

const { Text, Title } = Typography;

function removeTokenFromQuery(parameter: string): void {
  const urlWithoutToken: string = window.location.href
    // see https://stackoverflow.com/questions/1634748/how-can-i-delete-a-query-string-parameter-in-javascript
    .replace(new RegExp(`[?&]${parameter}=[^&#]*(#.*)?$`), '$1')
    .replace(new RegExp(`([?&])${parameter}=[^&]*&`), '$1');

  window.history.replaceState({}, '', urlWithoutToken);
}

interface LoginPageProps {
  onLogin: (email: string) => Promise<void>;
}

interface UserProfile {
  email: string;
  name: string;
  picture: string;
}

function NavHeader({
  loginMethodProps,
}: {
  loginMethodProps: {
    loginMethod: ReactNode;
    LinkWrapperComponent: React.ElementType;
  };
}) {
  return (
    <Navigation>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src="/grab.svg" width="57" />
        <Title
          style={{
            display: 'inline',
            margin: '0.5px 0 0 0',
            color: defaultPresetColors.green,
            opacity: 0.9,
            fontSize: 25.5,
          }}
        >
          X
        </Title>
      </div>
      <ListWrapper>
        <loginMethodProps.LinkWrapperComponent>
          {loginMethodProps.loginMethod}
        </loginMethodProps.LinkWrapperComponent>
      </ListWrapper>
    </Navigation>
  );
}

type OwnProps = LoginPageProps;

class LoginPage extends React.Component<
  OwnProps,
  { isLoading: boolean; triedOnce: boolean }
> {
  state = { isLoading: false, triedOnce: false };

  googleToken?: string;
  jwtToken?: string;
  concedoToken?: string;

  async componentDidMount() {
    const googleTokenParam = 'googleToken';
    const concedoTokenParam = 'code';
    const concedoStateParam = 'state';
    const jwtTokenParam = 'jwtToken';
    const expiryParam = 'expiry';

    const searchParams = new URLSearchParams(window.location.search);
    const googleToken: string = (this.googleToken =
      searchParams.get(googleTokenParam) ?? '');
    const jwtToken: string = (this.jwtToken =
      searchParams.get(jwtTokenParam) ?? '');
    const concedoToken: string = (this.concedoToken =
      searchParams.get(concedoTokenParam) ?? '');
    const email: string = searchParams.get('email') ?? '';
    const name: string = searchParams.get('name') ?? '';
    const image: string = searchParams.get('image') ?? '';

    const userInfo: GoogleInfo = {
      googleToken,
      email,
      name,
      image,
      source: getSource(),
    };

    if (this.googleToken) {
      this.setState({ isLoading: true });
      try {
        await Service.planner.login(userInfo);
        // removeTokenFromQuery should be called first, exactly in this order
        removeTokenFromQuery(googleTokenParam);
        this.props.onLogin(userInfo.email).catch((e) => {
          console.error(e);
        });
      } catch (e) {
        console.error(e);
        removeTokenFromQuery(googleTokenParam);
      } finally {
        this.setState({ isLoading: false });
      }
    }

    if (this.jwtToken) {
      this.setState({ isLoading: true });
      try {
        const expiry: number = parseInt(searchParams.get(expiryParam) ?? '');
        Service.saml.login(jwtToken, expiry);
        Service.cache.setSSOUserInfo(userInfo);
        removeTokenFromQuery(jwtTokenParam);
        removeTokenFromQuery(expiryParam);
        this.props.onLogin(userInfo.email).catch((e) => {
          console.error(e);
        });
      } catch (e) {
        console.error(e);
        removeTokenFromQuery(jwtTokenParam);
        removeTokenFromQuery(expiryParam);
      } finally {
        this.setState({ isLoading: false });
      }
    }

    if (this.concedoToken) {
      this.setState({ isLoading: true });
      try {
        const storedState: any = lscache.get(CONCEDO_STATE) || null;
        const storedCodeVerifier: any =
          lscache.get(CONCEDO_CODE_VERIFIER) || null;
        const storedRedirectURI: any =
          lscache.get(CONCEDO_REDIRECT_URI) || null;
        const storedClientID: any = lscache.get(CONCEDO_CLIENT_ID) || null;
        lscache.remove(CONCEDO_STATE);
        lscache.remove(CONCEDO_CODE_VERIFIER);
        lscache.remove(CONCEDO_REDIRECT_URI);
        lscache.remove(CONCEDO_CLIENT_ID);
        const concedoState: string = searchParams.get(concedoStateParam) ?? '';
        // OAuth state validation to prevent CSRF attacks
        if (concedoState !== storedState) {
          console.error('Invalid state');
          return;
        }
        if (!storedCodeVerifier || !storedRedirectURI || !storedClientID) {
          console.error('Invalid code verifier or redirectURI');
          return;
        }
        await Service.planner.loginConcedo({
          code: concedoToken,
          codeVerifier: storedCodeVerifier,
          redirectURI: storedRedirectURI,
          clientID: storedClientID,
        });

        removeTokenFromQuery(concedoTokenParam);

        const concedoUserInfo = Service.cache.getUserInfo();

        await this.props.onLogin(concedoUserInfo.email).catch((e) => {
          console.error(e);
        });

        const redirectUserRequestURI = lscache.get(REDIRECT_USER_REQUEST_URI);
        if (redirectUserRequestURI) {
          window.location.href = redirectUserRequestURI;
        }
        // Redirect will be handled by the app router
      } catch (e) {
        console.error(e);
        removeTokenFromQuery(concedoTokenParam);
      } finally {
        this.setState({ isLoading: false });
      }
    }
  }

  render(): ReactNode {
    if (this.state.isLoading) return <Spin />;

    return (
      <Wrapper>
        <NavHeader loginMethodProps={this.getLoginMethod()} />
        <NavSection>
          <HeaderTitle>
            The Product Configuration <br /> and Experimentation Platform
          </HeaderTitle>
          <HeaderSubtitle>
            Accelerate innovation and focus on key ideas evaluated <br />{' '}
            through thousands of controlled experiments.
          </HeaderSubtitle>
          <VideoWrapper playsInline autoPlay muted loop>
            <source src="/grabx-dashboard-prod.mov" type="video/mp4" />
          </VideoWrapper>
          <SwirlIcon src="grabber.svg" />
        </NavSection>

        <HeroSection>
          <Subtitle>
            <ColoredText color="#00B14F">Close the loop</ColoredText> on your
            product <br /> development lifecycle.
          </Subtitle>
          <FeatureWrapper>
            <FeatureCard>
              <IconContainer>
                <FeatureLogo>⚙️</FeatureLogo>
              </IconContainer>
              <FeatureTitle>Product Configuration</FeatureTitle>
              <FeatureSubtitle>
                Understand each configuration and how it affects the product.
              </FeatureSubtitle>
            </FeatureCard>
            <FeatureCard>
              <IconContainer>
                <FeatureLogo>🧪</FeatureLogo>
              </IconContainer>
              <FeatureTitle>Experimentation</FeatureTitle>
              <FeatureSubtitle>
                Unlimited number of A/B/n and Adaptive experiments at scale.
              </FeatureSubtitle>
            </FeatureCard>
            <FeatureCard>
              <IconContainer>
                <FeatureLogo>🚀</FeatureLogo>
              </IconContainer>
              <FeatureTitle>Feature Rollouts</FeatureTitle>
              <FeatureSubtitle>
                Rollout your features seamlessly and confidently.
              </FeatureSubtitle>
            </FeatureCard>
          </FeatureWrapper>
        </HeroSection>

        <HeroSection>
          <Subtitle>
            <ColoredText color="#00B14F">Empowered</ColoredText> by <br />{' '}
            state-of-the-art integrations.
          </Subtitle>
          <FeatureWrapper>
            <FeatureCard>
              <IconContainer>
                <FeatureLogo>🔮</FeatureLogo>
              </IconContainer>
              <FeatureTitle>Midas</FeatureTitle>
              <FeatureSubtitle>
                Select any Midas metric for your experiments.
              </FeatureSubtitle>
            </FeatureCard>
            <FeatureCard
              style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}
            >
              <IconContainer>
                <FeatureLogo>📈</FeatureLogo>
              </IconContainer>
              <FeatureTitle>Superset</FeatureTitle>
              <FeatureSubtitle>
                Automated analysis directly in Superset.
              </FeatureSubtitle>
            </FeatureCard>
            <FeatureCard>
              <IconContainer>
                <FeatureLogo>🎯</FeatureLogo>
              </IconContainer>
              <FeatureTitle>CDP</FeatureTitle>
              <FeatureSubtitle>
                Select any CDP segment in your experiments.
              </FeatureSubtitle>
            </FeatureCard>
          </FeatureWrapper>
        </HeroSection>

        <HeroSection
          style={{
            paddingBottom: 80,
          }}
        >
          <Subtitle>
            Frequently asked{' '}
            <ColoredText color="#00B14F">questions</ColoredText>
          </Subtitle>
          <Collapse
            size="large"
            style={{
              marginTop: 32,
              background: '#FFF',
              boxShadow: '0px 16px 24px 0px #161A1D1A',
            }}
            items={items}
            bordered={false}
            expandIcon={({ isActive }) => (
              <ClickRightUniversal
                style={{ fontWeight: 800, color: '#00B14F' }}
                rotate={isActive ? 90 : 0}
              />
            )}
          />
        </HeroSection>
      </Wrapper>
    );
  }

  getLoginMethod = (): {
    loginMethod: ReactNode;
    LinkWrapperComponent: React.ElementType;
  } => {
    const tenant = getTenant();

    if (isSSOLoginTenant()) {
      return {
        loginMethod: this.ssoLogin(tenant),
        LinkWrapperComponent: LinkWrapper,
      };
    }

    return {
      loginMethod: this.concedoAndGoogleLogin(tenant),
      LinkWrapperComponent: LinkWrapperConcedo,
    };
  };

  ssoLogin = (tenant: string): ReactNode => {
    return <SSOLogin service={tenant} />;
  };

  concedoAndGoogleLogin = (tenant: string): ReactNode => {
    return (
      <Flex gap={8} align={'center'}>
        <ConcedoLoginButton tenant={tenant} />
      </Flex>
    );
  };

  onGoogleLogin = async (response: CredentialResponse) => {
    const userProfile: UserProfile = jwt_decode(response.credential);
    const userInfo: GoogleInfo = {
      googleToken: response.credential,
      email: userProfile.email,
      name: userProfile.name,
      image: userProfile.picture,
      source: getSource(),
    };

    if (redirectTo) {
      const location: Location = window.location;
      let query = toQueryString(userInfo);
      if (redirectTo.includes('?')) {
        query = `&${query.slice(1)}`;
      }

      location.replace(`${redirectTo}${query}`);
    } else {
      try {
        await Service.planner.login(userInfo);
        await this.props.onLogin(userInfo.email);
        this.setState({ isLoading: false, triedOnce: true });
      } catch (e) {
        this.onGoogleLoginFailure();
      }
    }
  };
  onGoogleLoginFailure = (): void => {
    this.setState({ isLoading: false, triedOnce: true });
  };

  setLoadingTrue = (): void => this.setState({ isLoading: true });
}

const BaseHeaderText = styled(Text)`
  font-size: 16px;
`;

const BaseText = styled(Text)`
  font-size: 14px;
`;

const items: CollapseProps['items'] = [
  {
    key: '1',
    label: (
      <BaseHeaderText strong>
        How do I get my experiment approved?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        Your manager needs to have the necessary permissions in Concedo before
        approving your experiment.
      </BaseText>
    ),
  },
  {
    key: '2',
    label: (
      <BaseHeaderText strong>
        I want to extend a completed experiment, is that possible?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        No. If you wish to extend, kindly create a new experiment.
      </BaseText>
    ),
  },
  {
    key: '3',
    label: (
      <BaseHeaderText strong>
        Do I need to create multiple experiments for experimenting on different
        app versions?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        Depending on your use case, you might find Rollouts to suit your needs
        better. In general, experimenting on different constraints such as app
        version requires multiple experiments.
      </BaseText>
    ),
  },
  {
    key: '4',
    label: (
      <BaseHeaderText strong>
        What is the experiment auto-cancelling policy?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        Your experiment will be automatically cancelled after two working days
        if no approval is initiated by your manager.
      </BaseText>
    ),
  },
  {
    key: '5',
    label: (
      <BaseHeaderText strong>
        How to test the variable result with given facets?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        You may wish to go the the Variable&apos;s page and fill in the
        necessary details in the Test tab and hit the GetVariable button to
        retrieve the variable result.
      </BaseText>
    ),
  },
  {
    key: '6',
    label: (
      <BaseHeaderText strong>
        What is the order of the GetVariable resolve?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        Experiments are first priority and hence take precedence over Rollouts.
      </BaseText>
    ),
  },
  {
    key: '7',
    label: (
      <BaseHeaderText strong>
        How to exclude users based on my previous experiment results?
      </BaseHeaderText>
    ),
    children: (
      <BaseText>
        You may wish to create a blacklist group of users in CDP and use that
        group in your next experiment or rollout.
      </BaseText>
    ),
  },
];

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const ListWrapper = styled.ol`
  display: flex;
  margin-bottom: 0;
`;

const LinkWrapper = styled.li`
  display: inline-block;
  vertical-align: baseline;

  &:nth-child(3) {
    margin-right: 16px;
  }
`;

const LinkWrapperConcedo = styled.li`
  display: flex;
  vertical-align: baseline;
  align-items: center;

  &:nth-child(3) {
    margin-right: 16px;
  }
`;

const NavSection = styled.section``;

const SwirlIcon = styled.img`
  position: absolute;
  top: -40%;
  left: 38%;
`;

const Navigation = styled.nav`
  display: flex;
  background-color: #fff;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 24px;
  z-index: 2;
  min-width: 400px;
  max-width: 1120px;
  max-height: 65px;
  padding: 24px;
  border-radius: 16px;
  margin: auto;
  box-shadow: 0px 16px 24px 0px #161a1d1a;
`;

const HeaderTitle = styled.h1`
  position: relative;
  z-index: 1;
  margin: 80px auto auto auto;
  padding: 0;
  font-family: 'Inter';
  font-weight: 800;
  font-size: 48px;
  color: #3d3d3d;
  line-height: 48px;
  text-align: center;
  width: 800px;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  padding: 0;
  font-family: 'Inter';
  font-weight: 300;
  font-size: 20px;
  text-align: center;
  line-height: 24.2px;
  color: #707070;
  margin: 24px 0;
`;

const VideoWrapper = styled.video`
  display: block;
  position: relative;
  z-index: 1;
  width: 1120px;
  height: fit-content;
  margin: 16px auto 32px auto;
  border-radius: 16px;
  box-shadow: 0px 16px 24px 0px #161a1d1a;
`;

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  max-width: 1120px;
  margin: auto;
  margin-bottom: 80px;
`;

const Subtitle = styled.h2`
  margin: 0;
  padding: 0;
  font-family: 'Inter';
  font-weight: 800;
  font-size: 32px;
  line-height: 32px;
  text-align: center;
  color: #3d3d3d;
`;

const ColoredText = styled.span`
  color: ${(props) => props.color};
`;

const FeatureWrapper = styled.div`
  max-width: 1140px;
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`;

const FeatureCard = styled.div`
  background-color: #fff;
  border-radius: 16px;
  padding: 24px;
  max-width: 357px;
  height: 183px;
  box-shadow: 0px 16px 24px 0px #161a1d1a;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const FeatureLogo = styled.span`
  font-size: 24px;
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  background: #eeeff2;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  font-family: 'Inter';
  color: #3d3d3d;
`;

const FeatureSubtitle = styled.p`
  font-size: 16px;
  font-weight: 300;
  font-family: 'Inter';
  line-height: 20px;
  color: #707070;
`;

export default LoginPage;
